'use client';

import { ReleaseNoteSectionView, ReleaseNoteSectionViewOf } from 'bff';
import { ReleaseNoteSectionKind } from 'database';
import { ReactNode } from 'react';
import { cn } from 'ui';
import { editorialColumn, editorialWideColumn } from './editorial';
import { BulletsSection } from './sections/BulletsSection';
import { CalloutSection } from './sections/CalloutSection';
import { CardsSection } from './sections/CardsSection';
import { ComparisonSection } from './sections/ComparisonSection';
import { DecisionSection } from './sections/DecisionSection';
import { DemoSection } from './sections/DemoSection';
import { HeadingSection, ReleaseNoteSubhead } from './sections/HeadingSection';
import { ImageSection } from './sections/ImageSection';
import { SectionTitle } from './sections/SectionTitle';
import { StepsSection } from './sections/StepsSection';
import { TextSection } from './sections/TextSection';

type Placement = 'prose' | 'figure' | 'wide';

// How each kind sits on the page: copy in the reading column, graphics as
// figures under a ruled title, demos breaking out wider than the column.
const sectionLayouts: {
  [Kind in ReleaseNoteSectionKind]: {
    render: (props: { section: ReleaseNoteSectionViewOf<Kind> }) => JSX.Element;
    placement: Placement;
  };
} = {
  text: { render: TextSection, placement: 'prose' },
  heading: { render: HeadingSection, placement: 'prose' },
  callout: { render: CalloutSection, placement: 'prose' },
  bullets: { render: BulletsSection, placement: 'figure' },
  steps: { render: StepsSection, placement: 'figure' },
  comparison: { render: ComparisonSection, placement: 'figure' },
  cards: { render: CardsSection, placement: 'figure' },
  decision: { render: DecisionSection, placement: 'figure' },
  demo: { render: DemoSection, placement: 'wide' },
  image: { render: ImageSection, placement: 'figure' },
};

// TS cannot tie the record's key to the narrowed section, so the lookup is
// widened here, where the key is the section's own kind.
const layoutOf = (section: ReleaseNoteSectionView) =>
  sectionLayouts[section.kind] as {
    render: (props: { section: ReleaseNoteSectionView }) => JSX.Element;
    placement: Placement;
  };

export const ReleaseNoteFigure = ({
  title,
  caption,
  wide = false,
  children,
}: {
  title?: string | null;
  caption?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) => (
  <figure
    className={cn(
      wide ? editorialWideColumn : editorialColumn,
      'my-10 md:my-11',
    )}
  >
    {title && <SectionTitle>{title}</SectionTitle>}
    {children}
    {caption && (
      <figcaption className='mt-2.5 text-[0.8125rem] leading-[1.45] text-stone-600'>
        {caption}
      </figcaption>
    )}
  </figure>
);

// A section inside a figure that holds several, as an overview does.
export const ReleaseNoteSectionBody = ({
  section,
}: {
  section: ReleaseNoteSectionView;
}) => {
  const { render: Renderer } = layoutOf(section);

  return (
    <div className='mt-5 first:mt-0'>
      {section.title && (
        <h4 className='mb-2 text-sm font-semibold text-foreground'>
          {section.title}
        </h4>
      )}
      <Renderer section={section} />
    </div>
  );
};

export const ReleaseNoteSection = ({
  section,
}: {
  section: ReleaseNoteSectionView;
}) => {
  const { render: Renderer, placement } = layoutOf(section);

  if (placement === 'prose')
    return (
      <div className={editorialColumn}>
        {section.title && (
          <ReleaseNoteSubhead>{section.title}</ReleaseNoteSubhead>
        )}
        <Renderer section={section} />
      </div>
    );

  return (
    <ReleaseNoteFigure title={section.title} wide={placement === 'wide'}>
      <Renderer section={section} />
    </ReleaseNoteFigure>
  );
};
